import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AudioClassifier, FilesetResolver } from '@mediapipe/tasks-audio';
import { AudioSubtype } from './calls.service';

export interface AudioDetectionEvent {
    kind: 'start' | 'end';
    subtype: AudioSubtype;
    timestamp: number;
    confidence: number;
    avgConfidence?: number;
}

// Maps YAMNet display names to our internal subtypes
const CATEGORY_MAP: Record<string, AudioSubtype> = {
    'Baby cry, infant cry': 'cry',
    'Crying, sobbing': 'cry',
    'Whimper': 'whimper',
    'Wail, moan': 'wail',
    'Breathing': 'breathing',
    'Snoring': 'snoring',
    'Cough': 'cough',
    'Speech': 'speech',
    'Babbling': 'babbling'
};

const ENTER_THRESHOLD = 0.65;
const EXIT_THRESHOLD = 0.45;
const SMOOTHING_WINDOW = 5;
const COOLDOWN_MS = 10_000;
const CLASSIFY_INTERVAL_MS = 500;
const YAMNET_WINDOW_SAMPLES = 15_600; // 0.975s @ 16 kHz

interface SubtypeState {
    scores: number[];
    active: boolean;
    activeSince: number;
    peakConfidence: number;
    confidenceSum: number;
    scoreCount: number;
    lastEndedAt: number;
}

@Injectable({ providedIn: 'root' })
export class AudioClassifierService {
    public events$ = new Subject<AudioDetectionEvent>();

    private classifier: AudioClassifier | null = null;
    private audioCtx: AudioContext | null = null;
    private sourceNode: MediaStreamAudioSourceNode | null = null;
    private processor: ScriptProcessorNode | null = null;
    private silentSink: GainNode | null = null;
    private sampleBuffer: Float32Array | null = null;
    private sampleBufferFill = 0;
    private lastClassifyAt = 0;
    private state = new Map<AudioSubtype, SubtypeState>();
    private running = false;

    async start(stream: MediaStream): Promise<void> {
        if (this.running) return;
        this.running = true;

        try {
            if (!this.classifier) {
                const resolver = await FilesetResolver.forAudioTasks(
                    'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-audio/wasm'
                );
                this.classifier = await AudioClassifier.createFromOptions(resolver, {
                    baseOptions: {
                        modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/audio_classifier/yamnet/float32/1/yamnet.tflite'
                    },
                    maxResults: 5,
                    scoreThreshold: 0.1,
                    categoryAllowlist: Object.keys(CATEGORY_MAP)
                });
                console.log('[AudioClassifier] model ready');
            }

            const AudioCtxCtor = window.AudioContext || (window as any).webkitAudioContext;
            this.audioCtx = new AudioCtxCtor();
            const sampleRate = this.audioCtx.sampleRate;
            const targetSamples = Math.round(YAMNET_WINDOW_SAMPLES * (sampleRate / 16000));
            this.sampleBuffer = new Float32Array(targetSamples);
            this.sampleBufferFill = 0;

            this.sourceNode = this.audioCtx.createMediaStreamSource(stream);
            this.processor = this.audioCtx.createScriptProcessor(4096, 1, 1);
            this.silentSink = this.audioCtx.createGain();
            this.silentSink.gain.value = 0;

            this.processor.onaudioprocess = (e) => this.onAudioChunk(e.inputBuffer.getChannelData(0), sampleRate);

            this.sourceNode.connect(this.processor);
            this.processor.connect(this.silentSink);
            this.silentSink.connect(this.audioCtx.destination);
        } catch (err) {
            console.error('[AudioClassifier] start failed', err);
            this.running = false;
            this.teardownAudio();
        }
    }

    async stop(): Promise<void> {
        if (!this.running) return;
        this.running = false;

        // Close any active events as 'end'
        const now = Date.now();
        for (const [subtype, st] of this.state.entries()) {
            if (st.active) {
                const avg = st.scoreCount > 0 ? st.confidenceSum / st.scoreCount : st.peakConfidence;
                this.events$.next({ kind: 'end', subtype, timestamp: now, confidence: st.peakConfidence, avgConfidence: avg });
            }
        }
        this.state.clear();
        this.teardownAudio();
    }

    private teardownAudio() {
        try { this.processor?.disconnect(); } catch { /* noop */ }
        try { this.sourceNode?.disconnect(); } catch { /* noop */ }
        try { this.silentSink?.disconnect(); } catch { /* noop */ }
        this.processor = null;
        this.sourceNode = null;
        this.silentSink = null;
        if (this.audioCtx && this.audioCtx.state !== 'closed') {
            this.audioCtx.close().catch(() => { /* noop */ });
        }
        this.audioCtx = null;
        this.sampleBuffer = null;
        this.sampleBufferFill = 0;
    }

    private onAudioChunk(chunk: Float32Array, sampleRate: number) {
        if (!this.sampleBuffer) return;

        // Append to rolling buffer; shift when full
        const buf = this.sampleBuffer;
        const need = buf.length - this.sampleBufferFill;
        if (chunk.length <= need) {
            buf.set(chunk, this.sampleBufferFill);
            this.sampleBufferFill += chunk.length;
        } else {
            // Shift-left to make room (keeps rolling window)
            const overflow = chunk.length - need;
            buf.copyWithin(0, overflow);
            buf.set(chunk, buf.length - chunk.length);
            this.sampleBufferFill = buf.length;
        }

        if (this.sampleBufferFill < buf.length) return;

        const now = performance.now();
        if (now - this.lastClassifyAt < CLASSIFY_INTERVAL_MS) return;
        this.lastClassifyAt = now;

        this.runClassification(buf, sampleRate);
    }

    private runClassification(samples: Float32Array, sampleRate: number) {
        if (!this.classifier) return;
        let results;
        try {
            results = this.classifier.classify(samples, sampleRate);
        } catch (err) {
            console.warn('[AudioClassifier] classify failed', err);
            return;
        }
        if (!results?.length) return;

        // Collapse all categories into max score per subtype
        const frameScores = new Map<AudioSubtype, number>();
        for (const r of results) {
            for (const cat of r.classifications?.[0]?.categories ?? []) {
                const name = cat.categoryName;
                const subtype = CATEGORY_MAP[name];
                if (!subtype) continue;
                const score = cat.score ?? 0;
                const prev = frameScores.get(subtype) ?? 0;
                if (score > prev) frameScores.set(subtype, score);
            }
        }

        // Update per-subtype rolling buffer + state machine
        const allSubtypes = new Set<AudioSubtype>([...this.state.keys(), ...frameScores.keys()]);
        for (const subtype of allSubtypes) {
            const score = frameScores.get(subtype) ?? 0;
            this.advanceState(subtype, score);
        }
    }

    private advanceState(subtype: AudioSubtype, score: number) {
        let st = this.state.get(subtype);
        if (!st) {
            st = { scores: [], active: false, activeSince: 0, peakConfidence: 0, confidenceSum: 0, scoreCount: 0, lastEndedAt: 0 };
            this.state.set(subtype, st);
        }

        st.scores.push(score);
        if (st.scores.length > SMOOTHING_WINDOW) st.scores.shift();
        const mean = st.scores.reduce((a, b) => a + b, 0) / st.scores.length;

        const now = Date.now();

        if (!st.active) {
            const cooled = st.lastEndedAt === 0 || (now - st.lastEndedAt) >= COOLDOWN_MS;
            if (mean > ENTER_THRESHOLD && cooled) {
                st.active = true;
                st.activeSince = now;
                st.peakConfidence = mean;
                st.confidenceSum = mean;
                st.scoreCount = 1;
                this.events$.next({ kind: 'start', subtype, timestamp: now, confidence: mean });
            }
        } else {
            // Track peak + average while active
            if (mean > st.peakConfidence) st.peakConfidence = mean;
            st.confidenceSum += mean;
            st.scoreCount += 1;

            if (mean < EXIT_THRESHOLD) {
                const avg = st.confidenceSum / st.scoreCount;
                st.active = false;
                st.lastEndedAt = now;
                this.events$.next({ kind: 'end', subtype, timestamp: now, confidence: st.peakConfidence, avgConfidence: avg });
            }
        }
    }
}
